import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export const DOM_METHODS = [
  ["us_1oz", "US 1oz Non-Machinable Stamped (1-3 PCs) - $1.50"],
  ["us_2oz", "US 2oz Stamped (4-6 PCs) - $2.00"],
  ["us_tracked", "US Tracked (6+ PCs) - Starts at $5.00 and goes up"],
  ["ww_tracked", "WW Tracked - Exact amount will be given based on info provided"],
];
export const THANKS = "Thank you for submitting your DoMs form. Once your items have been packed up you will receive your final DoMs amount via DM";

const Pill = ({ on, onClick, testId, children }) => (
  <button type="button" data-testid={testId} aria-pressed={on} onClick={onClick}
    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-sky-400 bg-sky-500/30 text-white font-semibold" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
    <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${on ? "border-sky-300 bg-sky-400 text-zinc-950" : "border-zinc-600"}`}>{on && <Check className="h-3 w-3" />}</span>
    <span>{children}</span>
  </button>
);

const DomsDialog = ({ jid, again, onClose, onDone }) => {
  const [verified, setVerified] = useState(null);
  const [address, setAddress] = useState("");
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (verified === null) return toast.error("Tell us if your claim photos are correct");
    if (!method) return toast.error("Pick a DoM method");
    setBusy(true);
    try {
      await api.post(`/higher/joiners/${jid}/doms`, { photos_verified: verified, address, method });
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't submit DoMs form"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="doms-form-dialog" className="bg-zinc-950 border-zinc-800 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">DoMs Form</DialogTitle>
          <DialogDescription>Only admins can see your answers. {again ? "Please fill in all your answers again." : "It locks once you send it."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="space-y-2">
            <Label>Have you verified that your claim photos are correct?</Label>
            <div className="grid grid-cols-2 gap-2">
              <Pill on={verified === true} onClick={() => setVerified(true)} testId="doms-verified-yes">Yes</Pill>
              <Pill on={verified === false} onClick={() => setVerified(false)} testId="doms-verified-no">No</Pill>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Mailing Address</Label>
            <p className="text-xs text-zinc-400">Please write your name and address exactly as how it should be written, especially for those in other countries to ensure the mail is delivered correctly. Example: Areku Trades 123 Packing Lane No Money, KQ 01124 USA</p>
            <Textarea data-testid="doms-address-input" required minLength={5} maxLength={600} rows={4} value={address} onChange={(e) => setAddress(e.target.value)} className="bg-zinc-900" />
          </div>
          <div className="space-y-2">
            <Label>DoM Method Preferred</Label>
            <p className="text-xs text-zinc-400">Which DOM Election do you select for your items. Please be aware these Higher Store PCs are much thicker than regular PCs, more like Superstar PVC</p>
            <div className="grid gap-2">
              {DOM_METHODS.map(([k, label]) => <Pill key={k} on={method === k} onClick={() => setMethod(k)} testId={`doms-method-${k}`}>{label}</Pill>)}
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="doms-submit" className="w-full h-11 rounded-xl bg-white text-zinc-950 hover:bg-zinc-200 font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit DoMs form
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function HigherDomsForm({ j, onChanged }) {
  const [open, setOpen] = useState(false);
  const [justSent, setJustSent] = useState(false);
  if (!j.doms?.eligible) return null;
  if (j.doms.locked || justSent) {
    return (
      <div data-testid="doms-thanks" className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" /> {THANKS}
      </div>
    );
  }
  return (
    <>
      <button type="button" data-testid="doms-form-btn" onClick={() => setOpen(true)}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-600 to-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition-[transform,opacity] duration-200 hover:-translate-y-0.5 hover:opacity-90">
        <Mail className="h-4 w-4" /> {j.doms.submitted ? "Update DoMs Form" : "DoMs Form"}
      </button>
      {open && <DomsDialog jid={j.id} again={j.doms.submitted} onClose={() => setOpen(false)} onDone={() => { setOpen(false); setJustSent(true); toast.success("DoMs form submitted"); onChanged(); }} />}
    </>
  );
}
